import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveDictationDraft } from "@/components/panels/use-live-dictation-draft";
import {
  dictationMicState,
  isDictationSettled as isSettled,
} from "@/desktop/dictation-status";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { useDictation } from "@/desktop/use-dictation";
import { isTauri } from "@/lib/is-tauri";
import type { ToastReplyMic } from "./toast-reply-box";

/** Card-owned voice replies (spec §4.4). Only this mic claims a recording;
 *  the global shortcut always goes to Denker. */
export function useToastReplyDictation({
  id,
  value,
  onChange,
  onSubmit,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
}): { mic: ToastReplyMic | undefined; cancelOwnedRecording: () => void } {
  const status = useDictation((state) => state.status);
  const desktop = isTauri();
  const [owns, setOwns] = useState(false);
  const ownsRef = useRef(false);
  const previousStatusRef = useRef(status);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const draft = useLiveDictationDraft({ enabled: owns, value, onChange, targetId: id });
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const submitDictation = useCallback((text: string) => {
    const current = draftRef.current;
    // Text ahead of the transcript — typed before the click, or typed after
    // it but before the first partial arrived — means the user still wants
    // to review before it goes out, exactly as when they edited the draft.
    if (current.wasDraftEdited() || current.hasBaseText()) {
      current.commitFinalTranscript(text);
      return;
    }
    current.discardAcceptedDraft();
    onSubmitRef.current(text);
  }, []);

  // The claim outlives an error (released at idle, see the shared hook) so a
  // reader such as the compact widget can tell whose error it is.
  const dictationClaim = useCommentDictationClaim({
    id,
    disableScreenshotCapture: true,
    deliverTranscript: submitDictation,
  });

  // Ownership drives the mic UI and live draft, and drops the moment the
  // recording settles (success, native error, or never started).
  const releaseOwnership = useCallback(() => {
    ownsRef.current = false;
    setOwns(false);
  }, []);

  // A native error mid-recording (dictation-native-state-lifecycle.ts) moves
  // status straight to "error" with no explicit stop/cancel call from this
  // hook — only this reactive effect observes it and lets go of ownership.
  useEffect(() => {
    const previous = previousStatusRef.current;
    previousStatusRef.current = status;
    if (isSettled(status) && !isSettled(previous) && ownsRef.current) {
      releaseOwnership();
    }
  }, [releaseOwnership, status]);

  const { release: releaseClaim } = dictationClaim;
  const cancelOwnedRecording = useCallback(() => {
    if (!ownsRef.current) return;
    const dictation = useDictation.getState();
    if (!isSettled(dictation.status)) void dictation.cancel();
    // An explicit cancel (collapse) abandons the recording outright: release
    // the claim now too, rather than waiting for idle.
    releaseOwnership();
    releaseClaim();
  }, [releaseClaim, releaseOwnership]);

  const { toggle } = dictationClaim;
  const onClick = useCallback(() => {
    const settled = isSettled(useDictation.getState().status);
    if (!settled && !ownsRef.current) return;
    if (settled) {
      ownsRef.current = true;
      setOwns(true);
    }
    const toggled = toggle();
    if (!toggled) return;
    void toggled.finally(() => {
      if (isSettled(useDictation.getState().status) && ownsRef.current) {
        releaseOwnership();
      }
    });
  }, [releaseOwnership, toggle]);

  return {
    mic: desktop ? { ...dictationMicState(status, owns), onClick } : undefined,
    cancelOwnedRecording,
  };
}
